import {
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridColDef, GridRowProps } from "@mui/x-data-grid";
import { DataGridPremiumProps } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { motion } from "motion/react";
import React, { MutableRefObject, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import CommonGridTable from "@/components/CommonGridTable";
import PassengerCountColumn from "@/components/table/PassengerCountColumn";
import orderColumn from "@/components/table/orderColumn";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import { PlanLocationColumn } from "@/pages/Plannn/components/table/PlanLocationColumn";
import { PlanPickupColumn } from "@/pages/Plannn/components/table/PlanPickupColumn";
import { TrackingDragPayload, TrackingOverTarget, useTrackingDndActions } from "@/stores/use-tracking-dnd";
import {
    useTrackingActions,
    useTrackingDate,
    useTrackingExpandedRows,
    useTrackingIsFullScreen,
} from "@/stores/use-tracking-table";
import { IPlanPassenger, Task } from "@/types/tasks.types";

import { useTrackingBoardDnd } from "../../hooks/useTrackingBoardDnd";
import TrackingActionsColumn from "./TrackingActionsColumn";
import { TrackingDetailPanelColumn } from "./TrackingDetailPanelColumn";
import TrackingDragOverlay from "./TrackingDragOverlay";
import TrackingDriverColumn from "./TrackingDriverColumn";
import TrackingDroppableTaskRow from "./TrackingDroppableTaskRow";
import TrackingStatusColumn from "./TrackingStatusColumn";
import { TrackingStatusFilter } from "./TrackingStatusFilter";
import TrackingToolbar from "./TrackingToolbar";
import TrackingDetailTable from "./detailTable";

function TrackingTable({
    rows,
    loading,
    apiRef,
    allRows,
    reloadTable,
}: {
    rows: Task<IPlanPassenger<"api">>[];
    allRows: Task<IPlanPassenger<"api">>[];
    loading: boolean;
    apiRef: MutableRefObject<GridApiPremium>;
    reloadTable: () => void;
}) {
    const { t } = useTranslation();
    const { size } = useWindowDimensions();
    const { updateSelectedRows, setExpandedRows } = useTrackingActions();
    const expandedRows = useTrackingExpandedRows();

    const date = useTrackingDate();
    const { movePassenger } = useTrackingBoardDnd({ date });
    const { beginDrag, setTarget, resetSession } = useTrackingDndActions();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    useEffect(() => {
        return () => {
            resetSession();
        };
    }, [resetSession]);

    function handleDragStart({ active }: DragStartEvent) {
        const dragData = active.data?.current as TrackingDragPayload | undefined;
        if (!dragData) return;
        beginDrag(dragData);
    }

    function handleDragOver(event: DragOverEvent) {
        const dragData = event?.active?.data?.current as TrackingDragPayload | undefined;
        const target = event?.over?.data?.current as TrackingOverTarget | undefined;
        if (!dragData || !target || target.taskId === dragData.taskId) {
            setTarget(null);
            return;
        }
        setTarget(target);
    }

    function handleDragEnd(event: DragEndEvent) {
        resetSession();
        const dragData = event?.active?.data?.current as TrackingDragPayload | undefined;
        const target = event?.over?.data?.current as TrackingOverTarget | undefined;
        if (!dragData || !target || dragData.isFlightArrival !== target.isArrival) return;
        movePassenger({ dragData, targetTaskId: target.taskId });
    }

    function handleDragCancel() {
        resetSession();
    }

    const columns: GridColDef[] = useMemo<GridColDef[]>(
        () => [
            TrackingDetailPanelColumn({ rows: allRows }),
            {
                ...GRID_CHECKBOX_SELECTION_COL_DEF,
                headerClassName: "checkbox-header",
            },
            orderColumn(),
            TrackingStatusColumn({ t }),
            PlanPickupColumn({ t, editable: false }),
            PlanLocationColumn({
                t,
                field: "origin",
                editable: false,
                valueGetter: (row) => row.origin?.alias || "",
                headerName: "Alım Noktası",
            }),
            PlanLocationColumn({
                t,
                field: "destination",
                editable: false,
                valueGetter: (row) => row.destination?.alias || "",
                headerName: "Hedef Noktası",
            }),
            PassengerCountColumn({ t }),
            TrackingDriverColumn({ t }),
            TrackingActionsColumn({ allRows }),
        ],
        [size, t, allRows]
    );
    const getDetailPanelContent = useCallback<NonNullable<DataGridPremiumProps["getDetailPanelContent"]>>(
        ({ row }) => <TrackingDetailTable row={row} />,
        []
    );

    const isFullScreen = useTrackingIsFullScreen();
    return (
        <DndContext
            sensors={sensors}
            modifiers={[restrictToVerticalAxis]}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
        >
            <div className="col-span-12 flex w-full grow    overflow-auto  h-full  ">
                <motion.div
                    initial={{ height: "100%", position: "static", width: "100%" }}
                    animate={{
                        height: isFullScreen ? "100vh" : "100%",
                        width: isFullScreen ? "100vw" : "100%",
                        position: isFullScreen ? "fixed" : "static",
                        padding: isFullScreen ? (size.md ? 16 : 4) : 0,
                        top: 0,
                        left: 0,
                        background: isFullScreen ? "white" : "transparent",
                        zIndex: isFullScreen ? 1000 : "auto",
                    }}
                    transition={{ duration: 0.3 }}
                >
                    <CommonGridTable
                        toolbar={() => <TrackingToolbar rows={allRows} apiRef={apiRef} reloadTable={reloadTable} />}
                        loading={loading}
                        rows={rows}
                        columns={columns}
                        checkboxSelection
                        allRows={allRows}
                        api={apiRef}
                        getDetailPanelContent={getDetailPanelContent}
                        onRowSelectionModelChange={updateSelectedRows}
                        startElement={TrackingStatusFilter}
                        detailPanelExpandedRowIds={expandedRows}
                        onDetailPanelExpandedRowIdsChange={setExpandedRows}
                        CustomGridRow={TrackingDroppableTaskRow as React.ComponentType<GridRowProps>}
                    />
                </motion.div>
            </div>
            <TrackingDragOverlay />
        </DndContext>
    );
}

export default TrackingTable;
